'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { createClient } from '@/lib/supabase/client'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const router = useRouter()
  const supabase = createClient()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // サブドメインの必須入力チェック
    if (!subdomain.trim()) {
      setError('店舗サブドメインを入力してください')
      setLoading(false)
      return
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        setError(error.message)
        return
      }

      if (data.user) {
        // ログイン成功後にダッシュボードに自動遷移
        try {
          // ユーザー情報と店舗情報を取得してサブドメインを照合
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select(`
              *,
              shops!inner (
                id,
                subdomain
              )
            `)
            .eq('auth_user_id', data.user.id)
            .single()

          if (userError || !userData) {
            setError('ユーザー情報が見つかりません')
            return
          }

          // サブドメインの照合
          if (userData.shops.subdomain !== subdomain.trim()) {
            setError('入力されたサブドメインが正しくありません')
            return
          }

          // ロールに応じてリダイレクト
          switch (userData.role) {
            case 'super_admin':
              router.push('/admin/dashboard')
              break
            case 'manager':
              router.push(`/shop/${userData.shop_id}/dashboard`)
              break
            case 'cast':
              router.push(`/cast/${userData.shop_id}/dashboard`)
              break
            default:
              router.push('/')
          }
        } catch (userError) {
          console.error('ユーザー情報取得エラー:', userError)
          setError('ログイン処理中にエラーが発生しました')
        }
      }
    } catch (err) {
      setError('ログインに失敗しました')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="h-12 w-12 rounded-lg bg-navy-600"></div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            ログイン
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            アカウントをお持ちでない場合は{' '}
            <Link href="/signup" className="font-medium text-primary hover:text-primary/80">
              新規登録
            </Link>
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>アカウントにログイン</CardTitle>
            <CardDescription>
              メールアドレス、パスワード、店舗サブドメインを入力してください
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  メールアドレス
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1"
                  placeholder="example@example.com"
                  autoComplete="email"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  パスワード
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1"
                  placeholder="パスワード"
                  autoComplete="current-password"
                />
              </div>

              <div>
                <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700">
                  店舗サブドメイン <span className="text-red-500">*</span>
                </label>
                <Input
                  id="subdomain"
                  type="text"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value)}
                  required
                  className="mt-1"
                  placeholder="your-shop"
                />
                <p className="mt-1 text-xs text-gray-500">
                  ログインする店舗のサブドメインを入力してください
                </p>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? 'ログイン中...' : 'ログイン'}
              </Button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">または</span>
                </div>
              </div>

              <div className="mt-6 text-center space-y-2">
                <Link
                  href="/cast-login"
                  className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                >
                  キャストログイン
                </Link>
                <p className="text-xs text-gray-500">
                  パスワードを忘れた場合は管理者にお問い合わせください
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
